/** 分镜脚本节点 · 风格库（workflowStyle）、画幅比例与输出语言 */

import {
  STORYBOARD_TIMELINE_FORMAT_EXAMPLE,
  buildStoryboardTimelineFormatHint
} from '@/utils/storyboardTimelineFormat'

export const STORYBOARD_LANGUAGE_OPTIONS = [
  { id: 'zh', label: '中文' },
  { id: 'en', label: 'English' }
]

export const STORYBOARD_DEFAULT_LANGUAGE = 'zh'

/** 与视频节点 data.ratio 一致 */
export const STORYBOARD_ASPECT_RATIO_OPTIONS = [
  { id: '16:9', label: '16:9 横屏' },
  { id: '9:16', label: '9:16 竖屏' }
]

export const STORYBOARD_DEFAULT_ASPECT_RATIO = '16:9'

/** 分镜整理（↑）：DeepSeek 输出时间轴分镜稿 */
export const STORYBOARD_DEEPSEEK_SYSTEM_PROMPT = `你是专业分镜编剧与视频分镜策划。请结合「上游参考材料」与「用户分镜草稿」，输出一版可直接用于拍摄沟通与文生视频的时间轴分镜稿。

输出格式（必须严格遵守）：
- 全文为连续段落，每一段以「起始秒-结束秒：」开头（如 0-3秒：、3-4秒：），单段时长建议 1～15 秒；全片时间轴从 0 秒起连贯衔接，不重叠、不留空档（可合并或拆分镜头）。
- 每段在一行或数行内写清：景别/机位、运镜变化、主体动作与情绪；若有对白写「角色说：「…」」或「静静地说「…」」；段末用逗号串联参考说明，如「参考的分镜画面」「某某声音参考中的御姐音色」「背景参考」等（至少含「参考的分镜画面」）。
- 禁止输出表格、markdown 标题、镜号列表、JSON 或寒暄；直接给出分镜正文。

格式示例（内容须根据用户素材改写，勿照抄）：
${STORYBOARD_TIMELINE_FORMAT_EXAMPLE}`

/** 解析剧本 → 角色/道具/场景 + 镜头 JSON（供自动建图/视频节点） */
export const SCRIPT_TO_STORYBOARD_BUNDLE_JSON_PROMPT = `你是影视分镜、美术与视频生成策划。用户会提供「文学剧本」或「时间轴分镜稿」（段落以「0-3秒：」等形式起句，可能含上游参考资料）。
请输出**唯一一个 JSON 对象**（不要用 markdown 代码围栏，不要任何前言或后记），结构如下（键名必须一致）：
{
  "角色": [ { "名称": "简短中文名", "提示词": "用于 Seedream 文生图的中文提示（外貌服装体态，勿含镜号台词）" } ],
  "道具": [ { "名称": "…", "提示词": "…" } ],
  "场景": [ { "名称": "…", "提示词": "…" } ],
  "镜头": [
    {
      "镜号": "01",
      "durationSec": 5,
      "时间段": "0-3秒",
      "景别": "如：全景/中景/特写",
      "运镜": "如：固定/推/拉/摇",
      "画面描述": "构图、主体、光影氛围",
      "台词": "无 或 具体台词",
      "音效": "无 或 具体音效",
      "人物索引": [0],
      "道具索引": [0],
      "场景索引": [0],
      "videoPrompt": "与时间段一致的整段中文：以「0-3秒：」起句，含运镜、动作、台词与「参考的分镜画面」等说明（不要把 JSON 键名写进正文）"
    }
  ]
}
硬性规则：
1. "durationSec" 必须为 **1～15 的整数（秒）**，且与「时间段」起止秒差一致（不足 1 秒按 1 秒计）。**生成视频节点时**前端会将镜头合并为 **10～15 秒/节点**（短于 10 秒的连续段落会合并；超过 15 秒会拆条），模型仍可按时间轴原文逐段输出「镜头」。
2. 若输入为时间轴分镜稿：**每条「X-Y秒：」段落至少对应一条「镜头」**（勿把多条时间轴硬合成一条，除非单段已超过 15 秒需拆条）；「videoPrompt」须保留该段时间前缀与段内全文（可略润色，勿删对白与参考说明）。
3. "人物索引" / "道具索引" / "场景索引" 为**从 0 开始的整数数组**，分别对应上面 "角色" / "道具" / "场景" 数组的下标；本镜未出现则用 []。
4. 至少输出 1 条 "镜头"。若剧本中无明显某类资产，对应数组可为 []。
5. "提示词" 要具体可画；名称简短。若用户消息含「续集已定素材」且某资产名称与该块中已定案名称一致，则 JSON 中该条目的「提示词」**必须留空字符串 "" 或一字不改照抄块中摘要**，**禁止**根据剧本重新撰写或润色该提示词（前端会强制替换为画布节点上的真实提示词）。
6. "台词" 字段与 "videoPrompt" 必须一致：对白/旁白须写在 "台词" 中，且 **"videoPrompt" 内必须用自然语言复述或引用该对白**（若本镜无对白则 "台词" 填「无」、"videoPrompt" 中写明无对白即可），不得让对白只出现在 JSON 里而不进入 videoPrompt。
7. 若用户消息中含「续集已定素材」块：其中列出的角色/道具/场景在剧本中再次出现时，JSON 中**「名称」须与该块完全一致**（勿改字、勿换同义词）；新出现的角色/道具/场景照常新建「名称」与「提示词」。若与已定素材同名，前端将以**画布上对应图片节点内的提示词**覆盖 JSON 中的提示词，以保证与前一集一致。`

/** 旧版下拉风格 id → 风格库说明（仅迁移无 workflowStyle 的画布） */
const LEGACY_STYLE_HINTS = {
  cinematic: '写实电影感：自然光影、电影级构图与景深、克制调色。',
  anime: '动漫/二次元：清晰线稿或赛璐璐质感、鲜明配色、动感构图。',
  vertical_drama: '竖屏短剧：9:16 构图、人物特写、快切与强情绪对白。',
  documentary: '纪录片：纪实光影、自然光、真实环境细节。',
  commercial: '广告商业片：高完成度布光、节奏紧凑、画面干净有冲击力。',
  suspense: '悬疑氛围：低调光、高对比、压迫感构图。',
  warm_healing: '治愈清新：柔和自然光、暖色或低饱和、舒缓节奏。'
}

export function normalizeStoryboardLanguage(raw) {
  const id = String(raw || '')
    .trim()
    .toLowerCase()
  if (id === 'en' || id === 'english' || id === 'en-us' || id === 'en_us') return 'en'
  return 'zh'
}

export function resolveStoryboardWorkflowStyle(d) {
  if (!d || typeof d !== 'object') return { name: '', prompt: '' }
  const ws = d.workflowStyle && typeof d.workflowStyle === 'object' ? d.workflowStyle : null
  if (!ws) return { name: '', prompt: '' }
  return {
    name: String(ws.name || '').trim(),
    prompt: String(ws.prompt || '').trim()
  }
}

export function normalizeStoryboardAspectRatio(raw) {
  const s = String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '')
  if (s === '9:16' || s === '9x16' || s === '916' || s === 'vertical') return '9:16'
  return '16:9'
}

export function resolveStoryboardAspectRatio(d) {
  if (!d || typeof d !== 'object') return STORYBOARD_DEFAULT_ASPECT_RATIO
  const raw =
    d.storyboardAspectRatio != null
      ? d.storyboardAspectRatio
      : d.aspectRatio != null
        ? d.aspectRatio
        : ''
  return normalizeStoryboardAspectRatio(raw || STORYBOARD_DEFAULT_ASPECT_RATIO)
}

/** 图片节点 aspectKey 的 ratioSlug 部分 */
export function storyboardAspectRatioToImageRatioSlug(aspectRatio) {
  return normalizeStoryboardAspectRatio(aspectRatio) === '9:16' ? '9x16' : '16x9'
}

/** @param {string} storyboardRatio '16:9' | '9:16' */
export function buildImageAspectKeyFromStoryboardRatio(storyboardRatio, tier = '2k') {
  const slug = storyboardAspectRatioToImageRatioSlug(storyboardRatio)
  const t = String(tier || '2k').trim().toLowerCase()
  const tierNorm = t === '1k' || t === '4k' ? t : '2k'
  return `${slug}_${tierNorm}`
}

export function storyboardAspectRatioFromNode(sbNode) {
  if (!sbNode || sbNode.type !== 'storyboard' || !sbNode.data) {
    return STORYBOARD_DEFAULT_ASPECT_RATIO
  }
  return resolveStoryboardAspectRatio(sbNode.data)
}

export function resolveStoryboardLanguage(d) {
  const raw =
    d && d.storyboardLanguage != null
      ? d.storyboardLanguage
      : d && d.outputLanguage != null
        ? d.outputLanguage
        : ''
  return normalizeStoryboardLanguage(raw || STORYBOARD_DEFAULT_LANGUAGE)
}

export function isStoryboardEnglishOutput(langOrData) {
  if (langOrData && typeof langOrData === 'object') {
    return resolveStoryboardLanguage(langOrData) === 'en'
  }
  return normalizeStoryboardLanguage(langOrData) === 'en'
}

/** 整理分镜表 / 解析 JSON 时写入 user 的偏好块 */
export function buildStoryboardPreferencesUserBlock(d) {
  if (!d || typeof d !== 'object') return ''
  const parts = []
  const { name, prompt } = resolveStoryboardWorkflowStyle(d)
  if (name || prompt) {
    parts.push(name ? `视觉风格（风格库）：${name}` : '视觉风格（风格库）')
    if (prompt) {
      parts.push(
        `风格库提示词（须贯彻到分镜画面描述、角色/道具/场景文生图提示词与 videoPrompt 的构图、光影、色彩与质感）：\n${prompt}`
      )
    }
  } else {
    const legacyHint = legacyStoryboardStyleHintFromOldFields(d)
    if (legacyHint) parts.push(`视觉风格说明：${legacyHint}`)
  }
  const ar = resolveStoryboardAspectRatio(d)
  parts.push(
    ar === '9:16'
      ? '画幅比例：9:16 竖屏。分镜构图、角色/道具/场景文生图与视频生成均按竖屏画幅。'
      : '画幅比例：16:9 横屏。分镜构图、角色/道具/场景文生图与视频生成均按横屏画幅。'
  )
  const en = isStoryboardEnglishOutput(d)
  parts.push(
    en
      ? '输出语言：English。时间轴分镜段落、镜头「台词」、旁白、音效说明，以及 videoPrompt / 文生图「提示词」中的可读文案均使用英文（专有名词可保留原文）。'
      : '输出语言：简体中文。时间轴分镜段落、镜头「台词」、旁白、音效说明，以及 videoPrompt / 文生图「提示词」中的可读文案均使用简体中文。'
  )
  parts.push(buildStoryboardTimelineFormatHint(en))
  return parts.filter(Boolean).join('\n')
}

function legacyStoryboardStyleHintFromOldFields(d) {
  if (!d || typeof d !== 'object') return ''
  const pick = d.storyboardStylePick != null ? String(d.storyboardStylePick).trim() : ''
  if (pick === '__custom__') {
    const custom = String(d.storyboardStyleCustom || '').trim()
    return custom ? `用户自定义视觉风格：${custom}` : ''
  }
  if (pick && LEGACY_STYLE_HINTS[pick]) return LEGACY_STYLE_HINTS[pick]
  return String(d.storyboardStyle || '').trim()
}
